import { ChangeDetectionStrategy, Component, ElementRef, input, output, viewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';

/**
 * R26 (fix-parent-portal-enrollment-review 3.5): the portal's upload control —
 * a filled button naming what to attach, then the file's name with Replace /
 * Remove once it is on file. The component only picks the file; the host step
 * does the upload and feeds `fileName` back.
 */
@Component({
  selector: 'ui-upload-button',
  imports: [MatButtonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="upload">
      @if (fileName()) {
        <span class="file-name" data-testid="upload-file-name">{{ fileName() }}</span>
        <button mat-button type="button" [disabled]="busy()" (click)="pick()" data-testid="upload-replace">
          Replace
        </button>
        <button mat-button type="button" [disabled]="busy()" (click)="removed.emit()" data-testid="upload-remove">
          Remove
        </button>
      } @else if (outlined()) {
        <!-- U11 (3.6): the compact shared Add style (outlined, teal, small). -->
        <button
          mat-stroked-button
          class="gp-add"
          type="button"
          [disabled]="busy()"
          (click)="pick()"
          [attr.data-testid]="testId()"
        >
          <span class="gp-add-emoji" aria-hidden="true">{{ icon() }}</span>
          {{ label() }}
        </button>
      } @else {
        <!-- 3.25 (U41): an attach control is a secondary action — the aqua Add
             pill with a paperclip; only Save & continue is solid teal. -->
        <button
          mat-stroked-button
          class="gp-add"
          type="button"
          [disabled]="busy()"
          (click)="pick()"
          [attr.data-testid]="testId()"
        >
          <span class="gp-add-emoji" aria-hidden="true">{{ icon() }}</span>
          {{ label() }}
        </button>
      }
      <input
        #input
        class="hidden"
        type="file"
        [accept]="accept()"
        [multiple]="multiple()"
        [attr.data-testid]="testId() + '-input'"
        (change)="onChange($event)"
      />
    </div>
  `,
  styles: [
    `
      .upload {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
      }
      .file-name {
        font-weight: 700;
        color: var(--gp-ink);
        word-break: break-all;
      }
      .hidden {
        position: absolute;
        width: 1px;
        height: 1px;
        opacity: 0;
        pointer-events: none;
      }
    `,
  ],
})
export class UiUploadButtonComponent {
  readonly label = input.required<string>();
  /** 3.48: the glyph on the pill (the paperclip by default; photos pass 📸). */
  readonly icon = input('📎');
  readonly accept = input('application/pdf,image/jpeg,image/png,image/heic');
  /** U11 (3.6): render the compact outlined style instead of the filled pill. */
  readonly outlined = input(false);
  readonly multiple = input(false);
  /** The name of the file already uploaded ('' = nothing attached yet). */
  readonly fileName = input('');
  readonly busy = input(false);
  readonly testId = input('upload');

  readonly file = output<File>();
  readonly removed = output<void>();

  private readonly input = viewChild.required<ElementRef<HTMLInputElement>>('input');

  pick(): void {
    this.input().nativeElement.click();
  }

  onChange(event: Event): void {
    const el = event.target as HTMLInputElement;
    const file = el.files?.[0];
    el.value = '';
    if (file) this.file.emit(file);
  }
}
